import Analytics from '@/utils/analytics';
import PlusIcon from '@assets/icons/plus.svg';
import React, { useImperativeHandle, useRef, useState } from 'react';
import {
  TextInput as BaseTextInput,
  Text,
  TouchableOpacity,
  View,
} from 'react-native';
import { createStyles } from '@/contexts/theme-context';
import { FONTS, TYPOGRAPHY } from '@/styles';
import { TextArea } from '@/components/text-area';
import { TextInput, Props as TextInputProps } from '@/components/text-input';
import { TrackingData } from '@/utils/analytics/base-reporter';
import { useDynamicConfigValue } from '@/hooks/use-dynamic-config-value';

export interface SummaryTextInputRef {
  focus: () => void;
  measureLayout: View['measureLayout'];
}

interface SharedProps {
  disabled?: boolean;
  id: string;
  numberOfLines?: number;
  onBlur?: () => void;
  onChange: (newValue: string) => void;
  onFocus?: () => void;
  placeholder?: string;
  subtitle?: string;
  title: string;
  trackingData: TrackingData | null;
  value: string;
}

type ValidationProps =
  | {
      error: string | null;
      required: true;
    }
  | {
      error?: string | null;
      required?: false;
    };

type Props = SharedProps & ValidationProps;

export const SummaryTextInput = React.forwardRef<SummaryTextInputRef, Props>(
  (
    {
      disabled = false,
      error,
      id,
      numberOfLines = 1,
      onBlur,
      onChange,
      onFocus,
      placeholder,
      required = false,
      subtitle,
      title,
      trackingData,
      value,
    },
    ref,
  ) => {
    const [styles, theme] = useStyles();
    const orderSummarySectionsEnabled = useDynamicConfigValue(
      'order_summary_sections_enabled',
    );

    const startOpen = useRef(required || Boolean(value));
    const [editing, setEditing] = useState(startOpen.current);

    const containerRef = useRef<View>(null);
    const inputRef = useRef<BaseTextInput>(null);

    useImperativeHandle(ref, () => ({
      focus: () => inputRef.current?.focus(),
      measureLayout: (...args) => containerRef.current?.measureLayout(...args),
    }));

    const textInputProps: TextInputProps = {
      autoFocus: !startOpen.current,
      containerStyle: styles.inputContainer,
      editable: !disabled,
      error,
      inputRef,
      onBlur: () => {
        if (!value.trim() && !required) {
          setEditing(false);
        }

        onBlur?.();
      },
      onChangeText: onChange,
      onFocus,
      placeholder,
      showDoneButtonAccessoryView: true,
      style: TYPOGRAPHY.medium,
      testID: `summary-text-input-${id}`,
      value,
    };

    return (
      <View
        ref={containerRef}
        style={[
          styles.base,
          orderSummarySectionsEnabled ? styles.baseSections : null,
        ]}
      >
        {editing ? (
          <View style={[styles.button, styles.buttonExpanded]}>
            <Text style={[TYPOGRAPHY.small, FONTS.primarySemiBold]}>
              {required ? `${title}*` : title}
            </Text>
          </View>
        ) : (
          <TouchableOpacity
            disabled={disabled}
            onPress={() => {
              if (trackingData) {
                Analytics.track(
                  `${trackingData.name}.press`,
                  trackingData.properties,
                );
              }

              setEditing(true);
              inputRef.current?.focus();
            }}
            style={[styles.button, styles.buttonCollapsed]}
          >
            <View style={styles.buttonText}>
              <Text style={TYPOGRAPHY.pSemiBold}>{title}</Text>
              {subtitle ? (
                <Text style={[TYPOGRAPHY.small, styles.buttonDescription]}>
                  {subtitle}
                </Text>
              ) : null}
            </View>
            <PlusIcon fill={theme.colors.primary} height={24} width={24} />
          </TouchableOpacity>
        )}
        {editing ? (
          <View style={styles.inputRow}>
            {numberOfLines === 1 ? (
              <TextInput {...textInputProps} />
            ) : (
              <TextArea {...textInputProps} numberOfLines={numberOfLines} />
            )}
          </View>
        ) : null}
      </View>
    );
  },
);

const useStyles = createStyles((theme) => ({
  base: {
    backgroundColor: theme.colors.white,
    borderBottomColor: theme.colors.background,
    borderBottomWidth: 2,
  },
  baseSections: {
    backgroundColor: theme.colors.white,
    borderBottomWidth: 0,
    borderRadius: 16,
    marginBottom: 8,
    paddingHorizontal: 16,
  },
  button: {
    alignItems: 'center',
    flexDirection: 'row',
  },
  buttonCollapsed: {
    paddingVertical: 16,
  },
  buttonDescription: {
    color: theme.colors.inactiveText,
    marginTop: 2,
  },
  buttonExpanded: {
    paddingBottom: 6,
    paddingTop: 12,
  },
  buttonText: {
    flex: 1,
  },
  inputContainer: {
    borderColor: theme.colors.inactiveFill,
  },
  inputRow: {
    paddingBottom: 12,
  },
}));
